import * as React from "react";
import { TTargetTax } from "types/tax";
import { dollarFormatter } from "utils";
import { useTranslation } from "react-i18next";
import { IoCloseCircleOutline  } from "react-icons/io5";
import { useLineItemContext } from "../../context/LineItemContext";

type Props = {
  data: TTargetTax;
  total: { list: number; sale: number; install: number };
  taxableTotal: { list: number; sale: number; install: number };
  priceType?: "list" | "install" | "sale";
  removeTax?: () => void;
  isEdit?: boolean;
  isGroup?: boolean;
  selectedTaxGroup?: any;
};

const Tax = (props: Props) => {
  const { t } = useTranslation();
  const tBase = "containers.lineItems.tax";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { data, total, taxableTotal, priceType, removeTax, isEdit, isGroup, selectedTaxGroup } = props;

  const { showMath } = useLineItemContext();

  const { 
    tax_type: taxType,
    label: taxLabel,
  } = data;
  
  const currentPriceType = priceType || "list";
  const rate = data[`${currentPriceType}_rate`] || 0;
  const type = data[`${currentPriceType}_type`];
  
  // Determine the base total for calculation
  let baseTotal;
  
  if (taxType === "tax") {
    baseTotal = taxableTotal[currentPriceType];
  } else {
    baseTotal = total[currentPriceType];
  }

  // baseTotal is in cents, rate is in cents for fixed or percent value
  // Round individual tax to avoid floating point issues
  // Result is in cents (for dollarFormatter which expects cents)
  const computedTax = type === "fixed" 
    ? Math.round(rate)
    : Math.round((rate / 100) * baseTotal);
  const lineTotal = dollarFormatter(isNaN(computedTax) ? 0 : computedTax);
  const displayRate = isNaN(rate) ? 0 : rate;
  const displayType = type;
  const displayLabel = taxLabel;

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-200 hover:bg-slate-50">
      <div className="flex w-1/2">

        <div className="flex items-center">
          <span className={`mr-1 font-semibold bg-white border w-6 h-6 rounded-md flex items-center justify-center ${
            displayType === "percent" ? "text-blue-500" : "text-green-500"
          }`}>
            {displayType === "percent" ? "%" : "$"}
          </span>
        </div>
        <div>
          <div className="text-xs">{displayLabel}</div>
          <div className="text-sm text-slate-600">
            {displayType === "percent" 
              ? `(${parseFloat(displayRate.toString())}%)` 
              : dollarFormatter(displayRate)}
          </div>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <div className="flex flex-col items-end">
          <div className="flex items-center space-x-1">
            {showMath && (
              <div className="text-sm">
                {displayType === "fixed"
                  ? ""
                  : (
                    <>
                      <span className="text-slate-500">{`${parseFloat(displayRate.toString())}% ×`}</span>
                      <span className={`${taxType === "tax" ? "text-primary" : "text-secondary"}`}>{`  ${dollarFormatter(baseTotal)}`}</span>
                      <span className="text-slate-500">{" = " }</span>
                    </>
                  )}
              </div>
            )}
            <div>{lineTotal}</div>
          </div>
        </div>
        {isEdit && !isGroup && (
          <button onClick={removeTax} className="text-red-500 hover:text-red-700 rounded-full ">
            <IoCloseCircleOutline size={20} />
          </button>
        )}
      </div>


    </div>
  );
};

export default Tax;